import { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { Camera, Upload, ArrowLeft, Loader2, Check, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import {
  CurrencySelect,
  CURRENCY_NO_CONVERSION_NOTE,
} from '@/app/components/currency-select';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent } from '@/app/components/ui/card';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Textarea } from '@/app/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/app/components/ui/select';
import {
  useReceiptOCR,
  useCreateExpense,
  useUploadReceipt,
  useExpenseCategories,
} from '@/hooks/use-payroll';
import { usePersons } from '@/hooks/use-persons';
import { useOrgCurrency, useOrgToday } from '@/hooks/use-settings';
import type { ExpensePaymentMethod, Person } from '@/lib/types';

export function MobileExpenseCapturePage() {
  const navigate = useNavigate();
  const orgToday = useOrgToday();
  const orgCurrency = useOrgCurrency();
  const cameraRef = useRef<HTMLInputElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const ocr = useReceiptOCR();
  const createExpense = useCreateExpense();
  const uploadReceipt = useUploadReceipt();
  const { data: categories } = useExpenseCategories({ is_active: true });
  const { data: personsData } = usePersons({ page_size: 100 });

  const [capturedFile, setCapturedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [ocrDone, setOcrDone] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ocrConfidence, setOcrConfidence] = useState<number | null>(null);

  const [form, setForm] = useState({
    amount: '',
    description: '',
    vendor: '',
    date: orgToday,
    category: '',
    person: '',
    currency: '',
    payment_method: 'personal_card' as ExpensePaymentMethod,
    notes: '',
  });

  const handleCapture = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      setCapturedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
      setOcrDone(false);
      setError(null);

      try {
        const result = await ocr.mutateAsync(file);
        setOcrConfidence(result.confidence);
        setForm((prev) => ({
          ...prev,
          amount: result.amount ?? prev.amount,
          vendor: result.vendor ?? prev.vendor,
          date: result.date ?? prev.date,
          description: result.vendor
            ? `Receipt from ${result.vendor}`
            : prev.description,
        }));
        setOcrDone(true);
      } catch {
        setOcrDone(true);
      }

      e.target.value = '';
    },
    [ocr]
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.amount || !form.person || !form.category) {
      setError('Please fill in amount, worker, and category');
      return;
    }

    try {
      const expense = await createExpense.mutateAsync({
        person: Number(form.person),
        category: Number(form.category),
        date: form.date,
        description: form.description || 'Mobile expense',
        amount: form.amount,
        vendor: form.vendor || undefined,
        payment_method: form.payment_method,
        notes: form.notes || undefined,
        is_billable: true,
        is_reimbursable: true,
        currency: form.currency || undefined,
      });

      if (capturedFile) {
        await uploadReceipt.mutateAsync({ expenseId: expense.id, file: capturedFile });
      }

      navigate(`/payroll/expenses/${expense.id}`);
    } catch {
      setError('Failed to submit expense');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Header */}
      <div className="sticky top-0 z-10 bg-white border-b px-4 py-3 flex items-center gap-3">
        <Button variant="ghost" size="icon" asChild>
          <Link to="/payroll/expenses">
            <ArrowLeft className="h-5 w-5" />
          </Link>
        </Button>
        <h1 className="text-lg font-semibold">Quick Expense</h1>
      </div>

      <div className="p-4 space-y-4 max-w-lg mx-auto">
        {/* Camera Capture - Large Button */}
        {!capturedFile && (
          <div className="space-y-3">
            <button
              type="button"
              onClick={() => cameraRef.current?.click()}
              className="w-full aspect-[4/3] rounded-2xl border-2 border-dashed border-gray-300 bg-white flex flex-col items-center justify-center gap-3 active:bg-gray-50 transition-colors"
            >
              <Camera className="h-16 w-16 text-gray-400" />
              <span className="text-lg text-gray-500 font-medium">Tap to capture receipt</span>
            </button>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => fileRef.current?.click()}
            >
              <Upload className="h-4 w-4 mr-2" />
              Upload from gallery
            </Button>
            <input
              ref={cameraRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={handleCapture}
            />
            <input
              ref={fileRef}
              type="file"
              accept="image/*,.pdf"
              className="hidden"
              onChange={handleCapture}
            />
          </div>
        )}

        {/* Preview + OCR Status */}
        {capturedFile && (
          <Card>
            <CardContent className="p-3 space-y-3">
              {previewUrl && (
                <img
                  src={previewUrl}
                  alt="Receipt"
                  className="w-full rounded-lg max-h-48 object-cover"
                />
              )}
              {ocr.isPending && (
                <div className="flex items-center gap-2 text-sm text-blue-600">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Extracting receipt data...
                </div>
              )}
              {ocrDone && ocrConfidence !== null && ocrConfidence > 50 && (
                <div className="flex items-center gap-2 text-sm text-green-600">
                  <Check className="h-4 w-4" />
                  Extracted from receipt — please verify
                </div>
              )}
              {ocrDone && (ocrConfidence === null || ocrConfidence <= 50) && (
                <div className="flex items-center gap-2 text-sm text-yellow-600">
                  <AlertTriangle className="h-4 w-4" />
                  Could not extract data — please fill manually
                </div>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setCapturedFile(null);
                  setPreviewUrl(null);
                  setOcrDone(false);
                }}
              >
                Retake photo
              </Button>
            </CardContent>
          </Card>
        )}

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            {error}
          </div>
        )}

        {/* Expense Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Primary fields - always visible */}
          <div className="space-y-3">
            <div>
              <Label className="text-base">Amount *</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                inputMode="decimal"
                placeholder="0.00"
                value={form.amount}
                onChange={(e) => setForm((p) => ({ ...p, amount: e.target.value }))}
                className="text-2xl h-14 font-mono"
                required
              />
            </div>
            <div>
              <Label htmlFor="mobile-expense-currency">Currency</Label>
              <CurrencySelect
                id="mobile-expense-currency"
                value={form.currency || orgCurrency}
                onValueChange={(value) =>
                  setForm((p) => ({ ...p, currency: value }))
                }
              />
              <p className="mt-1 text-xs text-gray-500">
                {CURRENCY_NO_CONVERSION_NOTE}
              </p>
            </div>
            <div>
              <Label>Category *</Label>
              <Select
                value={form.category}
                onValueChange={(v) => setForm((p) => ({ ...p, category: v }))}
              >
                <SelectTrigger className="h-12">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories?.results?.map((cat) => (
                    <SelectItem key={cat.id} value={String(cat.id)}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Description</Label>
              <Input
                value={form.description}
                onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                placeholder="What was this for?"
                className="h-12"
              />
            </div>
          </div>

          {/* Advanced fields - collapsible */}
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
          >
            {showAdvanced ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
            {showAdvanced ? 'Less details' : 'More details'}
          </button>

          {showAdvanced && (
            <div className="space-y-3 pt-1">
              <div>
                <Label>Worker *</Label>
                <Select
                  value={form.person}
                  onValueChange={(v) => setForm((p) => ({ ...p, person: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select worker" />
                  </SelectTrigger>
                  <SelectContent>
                    {personsData?.results?.map((p: Person) => (
                      <SelectItem key={p.id} value={String(p.id)}>
                        {p.first_name} {p.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Vendor</Label>
                <Input
                  value={form.vendor}
                  onChange={(e) => setForm((p) => ({ ...p, vendor: e.target.value }))}
                  placeholder="Store or vendor"
                />
              </div>
              <div>
                <Label>Date</Label>
                <Input
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm((p) => ({ ...p, date: e.target.value }))}
                />
              </div>
              <div>
                <Label>Payment Method</Label>
                <Select
                  value={form.payment_method}
                  onValueChange={(v) =>
                    setForm((p) => ({ ...p, payment_method: v as ExpensePaymentMethod }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="personal_card">Personal Card</SelectItem>
                    <SelectItem value="personal_cash">Personal Cash</SelectItem>
                    <SelectItem value="company_card">Company Card</SelectItem>
                    <SelectItem value="petty_cash">Petty Cash</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea
                  value={form.notes}
                  onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
                  placeholder="Additional notes"
                  rows={2}
                />
              </div>
            </div>
          )}

          <Button
            type="submit"
            className="w-full h-14 text-lg"
            disabled={createExpense.isPending || uploadReceipt.isPending}
          >
            {createExpense.isPending || uploadReceipt.isPending
              ? 'Submitting...'
              : 'Submit Expense'}
          </Button>
        </form>
      </div>
    </div>
  );
}
